<div class="campaign-modal">
  <header class="campaign-modal__header">
    <div class="campaign-modal__header--titles">
      <span class="campaign-modal__header--titles---category">
        {{ 'support-campaigns.form.category' | translate }}
      </span>
      <h2 class="campaign-modal__header--titles---heading">
        @if (isViewMode) {
          {{ viewHeadingText }}
        } @else {
          {{ headingKeyOrText | translate }}
        }
      </h2>
    </div>

    <button
      type="button"
      class="campaign-modal__header--close"
      (click)="close()"
      [attr.aria-label]="'support-campaigns.form.close' | translate">
      <i class="agres-close" aria-hidden="true"></i>
    </button>
  </header>

  <form [formGroup]="campaignForm" (ngSubmit)="onSubmit()">
    <div class="campaign-modal__body">
      <div class="campaign-modal__field">
        <label class="campaign-modal__field--label" for="campaignTitle">
          {{ 'support-campaigns.form.title-label' | translate }}
          <span class="campaign-modal__field--required" aria-hidden="true">*</span>
        </label>
        <input
          id="campaignTitle"
          type="text"
          class="campaign-modal__field--input"
          formControlName="title"
          [maxlength]="titleMaxLength"
          [attr.aria-invalid]="showTitleRequiredError"
          [placeholder]="'support-campaigns.form.title-placeholder' | translate" />

        @if (showTitleRequiredError) {
          <span class="campaign-modal__field--error" role="alert">
            {{ 'support-campaigns.form.title-required' | translate }}
          </span>
        }
      </div>

      <div class="campaign-modal__field">
        <label class="campaign-modal__field--label" for="campaignDescription">
          {{ 'support-campaigns.form.description-label' | translate }}
          <span class="campaign-modal__field--required" aria-hidden="true">*</span>
        </label>
        <textarea
          id="campaignDescription"
          class="campaign-modal__field--textarea"
          formControlName="description"
          rows="4"
          [maxlength]="descriptionMaxLength"
          [attr.aria-invalid]="showDescriptionRequiredError"
          [placeholder]="'support-campaigns.form.description-placeholder' | translate"></textarea>

        <span class="campaign-modal__field--counter">
          {{
            'support-campaigns.form.description-counter'
              | translate: { current: descriptionLength, max: descriptionMaxLength }
          }}
        </span>

        @if (showDescriptionRequiredError) {
          <span class="campaign-modal__field--error" role="alert">
            {{ 'support-campaigns.form.description-required' | translate }}
          </span>
        }
      </div>

      <div class="campaign-modal__field">
        <label class="campaign-modal__field--label" for="campaignUrl">
          {{ 'support-campaigns.form.url-label' | translate }}
          <span class="campaign-modal__field--required" aria-hidden="true">*</span>
        </label>
        <input
          id="campaignUrl"
          type="url"
          inputmode="url"
          class="campaign-modal__field--input"
          formControlName="url"
          [maxlength]="urlMaxLength"
          [attr.aria-invalid]="showUrlRequiredError || showUrlInvalidError"
          [attr.aria-describedby]="'campaignUrlHint'"
          [placeholder]="'support-campaigns.form.url-placeholder' | translate" />

        <span id="campaignUrlHint" class="campaign-modal__field--counter">
          {{ 'support-campaigns.form.url-hint' | translate }}
        </span>

        @if (showUrlRequiredError) {
          <span class="campaign-modal__field--error" role="alert">
            {{ 'support-campaigns.form.url-required' | translate }}
          </span>
        } @else if (showUrlInvalidError) {
          <span class="campaign-modal__field--error" role="alert">
            {{ 'support-campaigns.form.url-invalid' | translate }}
          </span>
        }
      </div>

      <div class="campaign-modal__field">
        <label class="campaign-modal__field--label" for="campaignStatus">
          {{ 'support-campaigns.form.status-label' | translate }}
        </label>
        <select id="campaignStatus" class="campaign-modal__field--select" formControlName="status">
          @for (option of statusOptions; track option) {
            <option [value]="option">{{ statusLabelKey(option) | translate }}</option>
          }
        </select>
      </div>

      <div class="campaign-modal__field">
        <label class="campaign-modal__field--checkbox" for="campaignAnnounceOnLogin">
          <input id="campaignAnnounceOnLogin" type="checkbox" formControlName="announceOnLogin" />
          <span>{{ 'support-campaigns.form.announce-label' | translate }}</span>
        </label>

        <span class="campaign-modal__field--counter">
          {{ 'support-campaigns.form.announce-hint' | translate }}
        </span>
      </div>

      <div class="campaign-modal__field">
        <span class="campaign-modal__field--label">
          {{ 'support-campaigns.form.image-label' | translate }}
          @if (!isViewMode) {
            <span class="campaign-modal__field--required" aria-hidden="true">*</span>
          }
        </span>

        @if (previewUrl) {
          <div class="campaign-modal__image">
            <img
              class="campaign-modal__image--preview"
              [src]="previewUrl"
              [alt]="'support-campaigns.form.image-alt' | translate" />

            <div class="campaign-modal__image--info">
              <strong class="campaign-modal__image--info---name">
                @if (previewFileName) {
                  {{ previewFileName }}
                } @else {
                  {{ 'support-campaigns.form.image-default-name' | translate }}
                }
              </strong>
              <span class="campaign-modal__image--info---hint">
                {{ 'support-campaigns.form.image-hint' | translate: { max: imageMaxSizeMb } }}
              </span>
            </div>

            @if (!isViewMode) {
              <button type="button" class="campaign-modal__image--remove" (click)="removeImage()">
                {{ 'support-campaigns.form.image-remove' | translate }}
              </button>
            }
          </div>
        } @else if (isViewMode) {
          <p class="campaign-modal__image--empty">
            {{ 'support-campaigns.form.image-empty' | translate }}
          </p>
        } @else {
          <button type="button" class="campaign-modal__dropzone" (click)="imageInput.click()">
            <i class="agres-upload" aria-hidden="true"></i>
            <span class="campaign-modal__dropzone--label">
              {{ 'support-campaigns.form.image-select' | translate }}
            </span>
            <span class="campaign-modal__dropzone--hint">
              {{ 'support-campaigns.form.image-hint' | translate: { max: imageMaxSizeMb } }}
            </span>
          </button>
        }

        <input
          #imageInput
          type="file"
          class="campaign-modal__file-input"
          [accept]="imageAcceptAttribute"
          (change)="onFileSelected($event)" />

        @if (imageErrorKey) {
          <span class="campaign-modal__field--error" role="alert">
            {{ imageErrorKey | translate: { max: imageMaxSizeMb } }}
          </span>
        }
      </div>
    </div>

    @if (submitErrorKey) {
      <span class="campaign-modal__submit-error" role="alert">
        {{ submitErrorKey | translate }}
      </span>
    } @else if (!isViewMode && !canSubmit && !isSubmitting) {
      <span class="campaign-modal__submit-pending" role="status">
        {{ 'support-campaigns.form.required-pending' | translate }}
      </span>
    }

    <footer class="campaign-modal__footer">
      <button type="button" class="campaign-modal__footer--secondary" (click)="close()">
        @if (isViewMode) {
          {{ 'support-campaigns.form.close' | translate }}
        } @else {
          {{ 'support-campaigns.form.cancel' | translate }}
        }
      </button>

      @if (!isViewMode) {
        <button type="submit" class="campaign-modal__footer--primary" [disabled]="!canSubmit">
          @if (isCreateMode) {
            {{ 'support-campaigns.form.submit-create' | translate }}
          } @else {
            {{ 'support-campaigns.form.submit-edit' | translate }}
          }
        </button>
      }
    </footer>
  </form>
</div>
